import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Check, Copy, Trash2 } from "lucide-react";

import { TokensService } from "../../../../api/tokens.service";
import { ApiToken, CreatedApiToken } from "../../../../model/token.model";
import { Card, CardHeader, CardTitle, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import { Alert } from "../../../ui/alert";
import { Empty } from "../../../ui/empty";
import { Modal } from "../../../ui/modal";
import { Spinner } from "../../../ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../ui/alert-dialog";

const EXPIRY_NEVER = 'never' as const;
const EXPIRY_OPTIONS = [30, 90, 365];
const DEFAULT_EXPIRY = '90';

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : null;

const isExpired = (token: ApiToken) =>
  token.expiresAt !== null && new Date(token.expiresAt).getTime() < Date.now();

const ApiTokens = () => {
  const { formatMessage } = useIntl();

  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<string>(DEFAULT_EXPIRY);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiToken | null>(null);
  const [copied, setCopied] = useState(false);

  const [tokenToRevoke, setTokenToRevoke] = useState<ApiToken | null>(null);

  const loadTokens = useCallback(async () => {
    try {
      const { data } = await TokensService.list();
      setTokens(data);
      setError(null);
    } catch {
      setError(formatMessage({ id: 'API_TOKENS_LOAD_ERROR' }));
    } finally {
      setLoading(false);
    }
  }, [formatMessage]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const openModal = () => {
    setName("");
    setExpiry(DEFAULT_EXPIRY);
    setCreated(null);
    setCopied(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    if (created) {
      loadTokens();
    }
    setCreated(null);
  };

  const createToken = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const { data } = await TokensService.create(
        name.trim(),
        expiry === EXPIRY_NEVER ? null : Number(expiry)
      );
      setCreated(data);
      setError(null);
    } catch {
      setError(formatMessage({ id: 'API_TOKEN_CREATE_ERROR' }));
      setModalOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const copyToken = () => {
    if (!created) return;
    navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const revokeToken = async () => {
    if (!tokenToRevoke) return;
    try {
      await TokensService.revoke(tokenToRevoke.id);
      setError(null);
      loadTokens();
    } catch {
      setError(formatMessage({ id: 'API_TOKEN_REVOKE_ERROR' }));
    } finally {
      setTokenToRevoke(null);
    }
  };

  const statusBadge = (token: ApiToken) => {
    if (token.revoked) {
      return <Badge variant="destructive">{formatMessage({ id: 'TOKEN_REVOKED' })}</Badge>;
    }
    if (isExpired(token)) {
      return <Badge variant="secondary">{formatMessage({ id: 'TOKEN_EXPIRED' })}</Badge>;
    }
    return <Badge>{formatMessage({ id: 'TOKEN_ACTIVE' })}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{formatMessage({ id: 'API_TOKENS' })}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {formatMessage({ id: 'API_TOKENS_DESCRIPTION' })}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Alert variant="error" message={error} />}

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : tokens.length === 0 ? (
          <Empty description={formatMessage({ id: 'NO_API_TOKENS' })} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{formatMessage({ id: 'TOKEN_NAME' })}</TableHead>
                <TableHead>{formatMessage({ id: 'TOKEN' })}</TableHead>
                <TableHead>{formatMessage({ id: 'CREATED' })}</TableHead>
                <TableHead>{formatMessage({ id: 'LAST_USED' })}</TableHead>
                <TableHead>{formatMessage({ id: 'EXPIRES' })}</TableHead>
                <TableHead>{formatMessage({ id: 'STATUS' })}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map(token => (
                <TableRow key={token.id}>
                  <TableCell className="font-medium">{token.name}</TableCell>
                  <TableCell>
                    <code className="text-xs text-muted-foreground">{token.tokenPrefix}…</code>
                  </TableCell>
                  <TableCell>{formatDate(token.createdAt)}</TableCell>
                  <TableCell>
                    {formatDate(token.lastUsedAt) ?? formatMessage({ id: 'NEVER_USED' })}
                  </TableCell>
                  <TableCell>
                    {formatDate(token.expiresAt) ?? formatMessage({ id: 'EXPIRES_NEVER' })}
                  </TableCell>
                  <TableCell>{statusBadge(token)}</TableCell>
                  <TableCell className="text-right">
                    {!token.revoked && !isExpired(token) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={formatMessage({ id: 'REVOKE' })}
                        onClick={() => setTokenToRevoke(token)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Button onClick={openModal}>{formatMessage({ id: 'GENERATE_TOKEN' })}</Button>

        <Modal
          open={modalOpen}
          title={formatMessage({ id: created ? 'API_TOKEN_CREATED' : 'GENERATE_TOKEN' })}
          onCancel={closeModal}
          onOk={created ? closeModal : createToken}
          okText={formatMessage({ id: created ? 'DONE' : 'GENERATE_TOKEN' })}
          cancelText={formatMessage({ id: 'CANCEL' })}
          confirmLoading={creating}
          footer={created ? (
            <Button variant="segment" onClick={closeModal}>
              {formatMessage({ id: 'DONE' })}
            </Button>
          ) : undefined}
        >
          {created ? (
            <div className="space-y-3">
              <Alert
                variant="warning"
                message={formatMessage({ id: 'API_TOKEN_COPY_WARNING' })}
              />
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-muted px-3 py-2 text-xs break-all select-all">
                  {created.token}
                </code>
                <Button variant="outline" size="icon" onClick={copyToken}>
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {formatMessage({ id: 'TOKEN_NAME' })}
                </label>
                <Input
                  value={name}
                  autoFocus
                  placeholder={formatMessage({ id: 'TOKEN_NAME_PLACEHOLDER' })}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createToken()}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {formatMessage({ id: 'TOKEN_EXPIRATION' })}
                </label>
                <Select value={expiry} onValueChange={setExpiry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map(days => (
                      <SelectItem key={days} value={String(days)}>
                        {formatMessage({ id: 'EXPIRES_IN_DAYS' }, { days })}
                      </SelectItem>
                    ))}
                    <SelectItem value={EXPIRY_NEVER}>
                      {formatMessage({ id: 'EXPIRES_NEVER' })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </Modal>

        <AlertDialog
          open={tokenToRevoke !== null}
          onOpenChange={open => !open && setTokenToRevoke(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{formatMessage({ id: 'REVOKE' })}</AlertDialogTitle>
              <AlertDialogDescription>
                {formatMessage({ id: 'API_TOKEN_REVOKE_CONFIRM' }, { name: tokenToRevoke?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{formatMessage({ id: 'NO' })}</AlertDialogCancel>
              <AlertDialogAction onClick={revokeToken}>
                {formatMessage({ id: 'YES' })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default ApiTokens;
